import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {Controls, ControlButton, useReactFlow} from 'reactflow';

import * as analytics from 'analytics/Analytics';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import useReactFlowControlsXPosition from 'processes/graph-builder/flowchart-viz/useReactFlowControlsXPosition';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface ProcessFlowchartEditorControlsProps {
  editorEventGroupId?: string;
  actionMethod?: ActionMethod;
  modalType?: string;
}

export enum ProcessFlowchartEditorAdjustmentType {
  ZOOM_IN = 'ZOOM_IN',
  ZOOM_OUT = 'ZOOM_OUT',
  FIT_TO_VIEW = 'FIT_TO_VIEW',
  AUTOLAYOUT = 'AUTOLAYOUT',
}

export default function ProcessFlowchartEditorControls(
  props: ProcessFlowchartEditorControlsProps
): JSX.Element | null {
  const reactFlowControlsXPosition = useReactFlowControlsXPosition();
  const {zoomIn, zoomOut, fitView} = useReactFlow();

  if (reactFlowControlsXPosition == null) {
    return null;
  }

  return (
    <Controls
      className={css(styles.controls)}
      style={{left: -reactFlowControlsXPosition}}
      showZoom={false}
      showFitView={false}
      showInteractive={false}
    >
      <Tooltip title="Zoom in">
        <ControlButton
          onClick={() => {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ADJUSTED_CANVAS_WORKSPACE, {
              adjustment_type: ProcessFlowchartEditorAdjustmentType.ZOOM_IN,
              event_group_id: props.editorEventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
              action_method: props.actionMethod,
              modal_type: props.modalType,
            });
            zoomIn();
          }}
          {...getTestProps({component: 'ProcessFlowchartEditorControls', element: 'zoomInButton'})}
        >
          <BenchlingIcon icon="plus" />
        </ControlButton>
      </Tooltip>
      <Tooltip title="Zoom out">
        <ControlButton
          onClick={() => {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ADJUSTED_CANVAS_WORKSPACE, {
              adjustment_type: ProcessFlowchartEditorAdjustmentType.ZOOM_OUT,
              event_group_id: props.editorEventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
              action_method: props.actionMethod,
              modal_type: props.modalType,
            });
            zoomOut();
          }}
          {...getTestProps({component: 'ProcessFlowchartEditorControls', element: 'zoomOutButton'})}
        >
          <BenchlingIcon icon="minus" />
        </ControlButton>
      </Tooltip>
      <Tooltip title="Fit to view">
        <ControlButton
          onClick={() => {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ADJUSTED_CANVAS_WORKSPACE, {
              adjustment_type: ProcessFlowchartEditorAdjustmentType.FIT_TO_VIEW,
              event_group_id: props.editorEventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
              action_method: props.actionMethod,
              modal_type: props.modalType,
            });
            fitView(DEFAULT_FIT_VIEW_OPTIONS);
          }}
          {...getTestProps({component: 'ProcessFlowchartEditorControls', element: 'fitViewButton'})}
        >
          <BenchlingIcon icon="square-corners" />
        </ControlButton>
      </Tooltip>
    </Controls>
  );
}

const styles = StyleSheet.create({
  controls: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
  },
});
